import { CartAddEvent, CartErrorEvent } from '@theme/events';

const CART_DRAWER_OPEN_PARAM = 'cart_drawer';
const LAST_PRODUCT_URL_KEY = 'mijota-last-product-url';
const LIVE_REGION_ID = 'mijota-cart-drawer-status';
const PENDING_ATTRIBUTE = 'data-mijota-cart-pending';
const UPSELL_FORM_SELECTOR = '[data-mijota-cart-upsell-form]';
const UPSELL_BUTTON_SELECTOR = '[data-mijota-cart-upsell-button]';

const getCartAddUrl = () => window.Theme?.routes?.cart_add_url || '/cart/add.js';

const getCartUrl = () => window.Theme?.routes?.cart_url || '/cart';

const getCartPathname = () =>
  new URL(getCartUrl(), window.location.origin).pathname.replace(/\/$/, '') || '/cart';

const getCartJsonUrl = () => `${getCartPathname()}.js`;

const isProductPage = () => window.location.pathname.includes('/products/');

const getProductUrl = () => `${window.location.pathname}${window.location.search}`;

const rememberProductUrl = () => {
  try {
    sessionStorage.setItem(LAST_PRODUCT_URL_KEY, getProductUrl());
  } catch (_error) {
    // Le drawer reste fonctionnel meme si le stockage de session est bloque.
  }
};

const getCartDrawer = () => document.querySelector('cart-drawer-component');

const openCartDrawer = async () => {
  const drawer = getCartDrawer();
  if (!drawer) return false;

  if (customElements.get('cart-drawer-component')) {
    drawer.open?.();
    return true;
  }

  await customElements.whenDefined('cart-drawer-component');
  drawer.open?.();
  return true;
};

const getCartSectionIds = () =>
  Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);

const isCartAddForm = (form) => {
  const action = new URL(form.action || '/cart/add', window.location.origin);
  return (
    action.origin === window.location.origin &&
    action.pathname.replace(/\/$/, '') === `${getCartPathname()}/add`
  );
};

const parseJsonResponse = async (response) => {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch (_error) {
    return {};
  }
};

const announce = (message) => {
  let liveRegion = document.getElementById(LIVE_REGION_ID);

  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.id = LIVE_REGION_ID;
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    document.body.appendChild(liveRegion);
  }

  liveRegion.textContent = '';
  window.requestAnimationFrame(() => {
    liveRegion.textContent = message;
  });
};

const setSubmitterBusy = (submitter, busy) => {
  if (!(submitter instanceof HTMLElement)) return;

  submitter.toggleAttribute('aria-busy', busy);

  if ('disabled' in submitter) {
    submitter.disabled = busy;
  }
};

const getUpsellButton = (form) => form.querySelector(UPSELL_BUTTON_SELECTOR);

const setUpsellState = (form, state, message = '') => {
  const button = getUpsellButton(form);
  const label = button?.querySelector('[data-mijota-cart-upsell-label]');
  const error = form.querySelector('[data-mijota-cart-upsell-error]');

  form.dataset.mijotaCartState = state;

  if (button && label) {
    button.dataset.defaultLabel ||= label.textContent?.trim() || '+ Ajouter';

    if (state === 'loading') {
      label.textContent = 'Ajout…';
    } else if (state === 'success') {
      label.textContent = 'Ajouté ✓';
      button.setAttribute('aria-pressed', 'true');
    } else {
      label.textContent = button.dataset.defaultLabel;
    }
  }

  if (error) {
    error.textContent = message;
    error.classList.toggle('hidden', !message);
  }
};

const captureUpsellContext = (form) => ({
  scrollTop: form.closest('.mijota-cart-scroll-area')?.scrollTop || 0,
  variantId: String(new FormData(form).get('id') || ''),
});

const restoreUpsellContext = ({ scrollTop, variantId }) => {
  window.requestAnimationFrame(() => {
    const drawer = getCartDrawer();
    const scrollArea = drawer?.querySelector('.mijota-cart-scroll-area');
    const button = drawer?.querySelector(`${UPSELL_BUTTON_SELECTOR}[data-variant-id="${variantId}"]`);

    if (scrollArea) scrollArea.scrollTop = scrollTop;
    if (button instanceof HTMLElement) button.focus({ preventScroll: true });
  });
};

const getCartErrorMessage = (response = {}) => {
  if (Number(response.status) === 422) {
    return 'Cet article n’est plus disponible dans cette quantité.';
  }

  return 'Impossible d’ajouter cet article. Veuillez réessayer.';
};

const dispatchCartError = (form, response) => {
  const message = getCartErrorMessage(response);
  announce(message);
  form.dispatchEvent(
    new CartErrorEvent(form.id || 'mijota-cart-drawer-only', message, response.description, response.errors)
  );
};

const handleCartAddFormSubmit = async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (form.closest('product-form-component')) return;
  if (!isCartAddForm(form)) return;

  event.preventDefault();

  if (!form.checkValidity()) {
    form.reportValidity();
    return;
  }

  if (form.hasAttribute(PENDING_ATTRIBUTE)) return;

  const submitter = event.submitter;
  const isUpsellForm = form.matches(UPSELL_FORM_SELECTOR);
  const upsellContext = isUpsellForm ? captureUpsellContext(form) : null;
  form.setAttribute(PENDING_ATTRIBUTE, 'true');
  setSubmitterBusy(submitter, true);
  if (isUpsellForm) setUpsellState(form, 'loading');

  try {
    const formData = new FormData(form);
    const sectionIds = getCartSectionIds();

    if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) {
      if (submitter.name && submitter.value && !formData.has(submitter.name)) {
        formData.append(submitter.name, submitter.value);
      }
    }

    if (sectionIds.length) {
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const addResponse = await fetch(getCartAddUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    });

    const addedItem = await parseJsonResponse(addResponse);

    if (!addResponse.ok || addedItem.status) {
      if (isUpsellForm) setUpsellState(form, 'error', getCartErrorMessage(addedItem));
      dispatchCartError(form, addedItem);
      return;
    }

    if (isProductPage()) rememberProductUrl();

    let cart = {};
    try {
      const cartResponse = await fetch(getCartJsonUrl(), {
        headers: {
          Accept: 'application/json',
        },
      });

      if (cartResponse.ok) cart = await cartResponse.json();
    } catch (cartError) {
      console.warn('Article ajouté, mais le total du panier n’a pas pu être relu.', cartError);
    }

    if (isUpsellForm) setUpsellState(form, 'success');

    form.dispatchEvent(
      new CartAddEvent(cart, String(formData.get('id') || form.id || ''), {
        source: isUpsellForm ? 'mijota-cart-upsell' : 'mijota-cart-drawer-only',
        itemCount: cart.item_count,
        variantId: String(formData.get('id') || ''),
        sections: addedItem.sections || {},
      })
    );

    announce('Article ajouté au panier.');
    if (upsellContext) {
      restoreUpsellContext(upsellContext);
    } else {
      await openCartDrawer();
    }
  } catch (error) {
    console.error(error);
    if (isUpsellForm) setUpsellState(form, 'error', getCartErrorMessage());
    dispatchCartError(form, {});
  } finally {
    form.removeAttribute(PENDING_ATTRIBUTE);
    setSubmitterBusy(submitter, false);
  }
};

const openDrawerFromUrl = async () => {
  const url = new URL(window.location.href);
  if (
    url.searchParams.get(CART_DRAWER_OPEN_PARAM) !== 'open' &&
    url.searchParams.get('open_cart_drawer') !== '1'
  ) {
    return;
  }

  const opened = await openCartDrawer();
  if (!opened) return;

  url.searchParams.delete(CART_DRAWER_OPEN_PARAM);
  url.searchParams.delete('open_cart_drawer');
  window.history.replaceState(window.history.state, '', url.toString());
};

if (isProductPage()) rememberProductUrl();

document.addEventListener('submit', handleCartAddFormSubmit);
openDrawerFromUrl();
